/* eslint-disable react-hooks/exhaustive-deps */
import React, { ComponentType, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {  TenantDiscoveryResponse } from 'api';
import { logIn } from 'common/config/urls';
import { useTenantDiscovery } from 'services/hooks';
import { useIsUserLoggedIn, useVerifyAccessToken } from 'services/hooks/auth';

interface CustomRouteElementProps {
  component?: ComponentType<any>;
  isPrivate?: boolean;
  layout?:
    | ComponentType<{ children: React.ReactNode }>
    | ((props: { children: React.ReactNode }) => React.ReactElement);
}

// Helper component that returns the element for use in Route components
export const CustomRouteElement = (props: CustomRouteElementProps) => {
  const isUserLoggedIn = useIsUserLoggedIn();
  const verifyAccessToken = useVerifyAccessToken();
  const { discoverTenantBySlug, needsDiscovery } = useTenantDiscovery({ location: globalThis.location.href });
  const [verified, setVerified] = useState(false);
  const accessToken = localStorage.getItem('access');
  const location = useLocation();

  const { layout: Layout, component: Component, isPrivate = false } = props;

  useEffect(() => {
    const verifyFlowToFollow = async () => {
      const flow = await discoverTenantBySlug();
      const postDiscoveryFlow = flow?.post_discovery_flow as string | undefined;
      if (flow && postDiscoveryFlow === TenantDiscoveryResponse.post_discovery_flow.SAML) {
        // browser redirect to SSO login URL
        globalThis.location.href = flow.login_url as string;
      }
    };
    if (!isUserLoggedIn && !accessToken && needsDiscovery) {
      verifyFlowToFollow();
    }

    // Log in the user if has valid access token
    if (!isUserLoggedIn && accessToken && !verified) {
      verifyAccessToken();
      setVerified(true);
    }

    return () => {
      setVerified(false);
    };
  }, []);

  if (needsDiscovery && !accessToken) {
    return null;
  }

  // Protect private page from unauthorized users
  if (!isUserLoggedIn && !accessToken && isPrivate) {
    return <Navigate to={logIn} state={{ from: location }} replace />;
  }

  if (Layout && Component) {
    return (
      <Layout>
        <Component />
      </Layout>
    );
  }

  return Component ? <Component /> : <></>;
};

// Legacy interface for backward compatibility
interface CustomRouteProps {
  path: string;
  component?: ComponentType<any>;
  isPrivate?: boolean;
  layout?:
    | ComponentType<{ children: React.ReactNode }>
    | ((props: { children: React.ReactNode }) => React.ReactElement);
  exact?: boolean;
}

// This is kept for compatibility but should not be used as direct child of Routes
const CustomRoute = (props: CustomRouteProps) => (
  // This component is deprecated in React Router v6 context
  // Use CustomRouteElement instead
  <CustomRouteElement {...props} />
);
export default CustomRoute;
